import type { Dispatch, FC, SetStateAction } from 'react';
import { VStack } from '@chakra-ui/react';

import SunshineCard from 'components/_core/SunshineCard';
import { BANK_ACCOUNT_ILLUSTRATIONS } from 'components/BankAccountIcon/constants';
import Loader from 'components/Loader';

import { type ClaimRequestsRequestListPageInfo } from '../../../../hooks/useManagedUserExpenseClaimRequests';
import { type ExpenseClaimRequestsListItemRequestFragment } from '../../../../hooks/useManagedUserExpenseClaimRequests/managedUserExpenseClaimRequests.gql';
import { type Tab } from '../../filters';
import EmptyView from '../EmptyView';
import { ExpenseClaimRequestListItem } from './components/ExpenseClaimRequestListItem';
import StatusBar from './components/StatusBar';
import locales from './locales';

interface ExpenseClaimsRequestsListProps {
  selectedExpenseClaimRequestId?: string;
  requests: ExpenseClaimRequestsListItemRequestFragment[];
  selectedTab: Tab;
  onSelectedTabChange: Dispatch<SetStateAction<Tab>>;
  isLoading: boolean;
  isLoadingMore: boolean;
  pageInfo: ClaimRequestsRequestListPageInfo;
}

const getTitle = (tab: Tab) => {
  switch (tab) {
    case 'closed':
      return locales.emptyList.closed;
    case 'pending':
    default:
      return locales.emptyList.pending;
  }
};

const ExpenseClaimRequestsList: FC<ExpenseClaimsRequestsListProps> = ({
  isLoading,
  isLoadingMore,
  onSelectedTabChange,
  pageInfo,
  requests,
  selectedExpenseClaimRequestId,
  selectedTab,
}) => {
  const hasRequests = requests.length > 0;
  return (
    <VStack alignItems="flex-start" spacing="space-24">
      <StatusBar onTabChange={onSelectedTabChange} selected={selectedTab} />
      {!isLoadingMore && isLoading ? (
        <Loader />
      ) : (
        <>
          {hasRequests ? (
            <SunshineCard.Group>
              {requests.map((request) => (
                <ExpenseClaimRequestListItem
                  isSelected={
                    selectedExpenseClaimRequestId ===
                    request.teamMembersRequestId
                  }
                  key={request.teamMembersRequestId}
                  request={request}
                />
              ))}
            </SunshineCard.Group>
          ) : (
            <>
              {!pageInfo.hasNextPage ? (
                <EmptyView
                  imageSrc={BANK_ACCOUNT_ILLUSTRATIONS['floating-cash']}
                  marginX="auto"
                  maxWidth={372}
                  title={getTitle(selectedTab)}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </VStack>
  );
};

export default ExpenseClaimRequestsList;
